import React, { useState, useRef, useCallback, useEffect } from "react";
import { Flex, Text, IconButton, Tooltip, Button, AlertDialog, Checkbox } from "@radix-ui/themes";
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";

const MINUTES_IN_DAY = 24 * 60;
const DAY_START_HOUR = 4; // 4AM
const MIN_PERIOD_MINUTES = 60;

function minutesToTime(minutes) {
  const totalMinutes = (minutes + DAY_START_HOUR * 60) % MINUTES_IN_DAY;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function minutesToFullTimeString(minutes) {
  const totalMinutes = (minutes + DAY_START_HOUR * 60) % MINUTES_IN_DAY;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
}

function minutesToDisplay(minutes) {
  const totalMinutes = (minutes + DAY_START_HOUR * 60) % MINUTES_IN_DAY;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const period = hours >= 12 ? "PM" : "AM";
  const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  if (mins === 0) return `${displayHour}${period}`;
  return `${displayHour}:${String(mins).padStart(2, "0")}${period}`;
}

function timeToMinutes(timeString) {
  if (!timeString) return 0;
  let [hours, minutes] = timeString.split(":").map(Number);
  let totalMinutes = ((hours * 60 + minutes) - DAY_START_HOUR * 60 + MINUTES_IN_DAY) % MINUTES_IN_DAY;
  return totalMinutes;
}

const PeriodSelector = ({ periods, selectedPeriod, onPeriodClick, onAddPeriod, onDeletePeriod, onEditPeriodBoundary, resetUnsavedChanges, isPeriodLoading }) => {
  // Convert periods to internal minutes representation
  const [segments, setSegments] = useState(() => {
    if (periods.length === 1) {
      return [{ id: periods[0].id, startMins: 0, endMins: MINUTES_IN_DAY }];
    }
    return periods.map((p, i) => ({
      id: p.id,
      startMins: timeToMinutes(p.start_time),
      endMins: i === periods.length - 1 ? MINUTES_IN_DAY : timeToMinutes(p.end_time),
    }));
  });
  
  const segmentsRef = useRef(segments);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const containerRef = useRef(null);
  const [truncatedSegments, setTruncatedSegments] = useState(new Set());
  
  // Track if user has made changes that haven't been saved yet
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Add refs to track drag state and cleanup functions
  const dragStateRef = useRef({ isDragging: false, segmentIndex: -1, cleanup: null });

  // Reset unsaved changes when parent indicates save is complete
  useEffect(() => {
    if (resetUnsavedChanges) {
      setHasUnsavedChanges(false);
    }
  }, [resetUnsavedChanges]);

  // Check for text truncation
  const checkTextTruncation = useCallback(() => {
    const newTruncated = new Set();
    segments.forEach((segment, index) => {
      const element = document.querySelector(`[data-segment-id="${segment.id}"] span`);
      if (element && element.scrollWidth > element.offsetWidth) {
        newTruncated.add(segment.id);
      }
    });
    setTruncatedSegments(newTruncated);
  }, [segments]);

  // Check truncation on mount and when segments change
  useEffect(() => {
    const timeoutId = setTimeout(checkTextTruncation, 0);
    return () => clearTimeout(timeoutId);
  }, [checkTextTruncation]);

  // Check truncation on window resize
  useEffect(() => {
    const handleResize = () => checkTextTruncation();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkTextTruncation]);

  // Always keep segmentsRef in sync with segments
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  // Keep segments in sync with periods prop, but only if no unsaved changes exist
  useEffect(() => {
    // If periods length changed (e.g., after deletion), force reset unsaved changes and update segments
    if (!dragging && !dragStateRef.current.isDragging) {
      if (hasUnsavedChanges && segments.length !== periods.length) {
        setHasUnsavedChanges(false);
      }
      if (!hasUnsavedChanges) {
        if (periods.length === 1) {
          const newSegs = [{ id: periods[0].id, startMins: 0, endMins: MINUTES_IN_DAY }];
          setSegments(newSegs);
          segmentsRef.current = newSegs;
        } else {
          const newSegs = periods.map((p, i) => ({
            id: p.id,
            startMins: timeToMinutes(p.start_time),
            endMins: i === periods.length - 1 ? MINUTES_IN_DAY : timeToMinutes(p.end_time),
          }));
          setSegments(newSegs);
          segmentsRef.current = newSegs;
        }
      }
    }
  }, [periods, dragging, hasUnsavedChanges, segments.length]);

  // Handle visibility change to complete drag operation if tab becomes hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && dragStateRef.current.isDragging) {
        // Complete the drag operation
        if (dragStateRef.current.cleanup) {
          dragStateRef.current.cleanup();
        }
      }
    };

    // Handle focus/blur events as additional safeguard
    const handleBlur = () => {
      if (dragStateRef.current.isDragging) {
        if (dragStateRef.current.cleanup) {
          dragStateRef.current.cleanup();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleBorderMouseDown = useCallback((e, segmentIndex) => {
    if (segmentIndex === segmentsRef.current.length - 1) return;
    e.preventDefault();
    e.stopPropagation();
    
    setDragging(true);
    dragStateRef.current.isDragging = true;
    dragStateRef.current.segmentIndex = segmentIndex;

    const handleMouseMove = (moveEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = moveEvent.clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, x / rect.width));
      const rawTime = percentage * MINUTES_IN_DAY;
      const nearestHour = Math.round(rawTime / 60) * 60;
      const snapThreshold = 30;
      const distanceToNearestHour = Math.abs(rawTime - nearestHour);
      const newTime = distanceToNearestHour <= snapThreshold ? nearestHour : Math.round(rawTime / 60) * 60;
      
      // Clamp
      const segs = segmentsRef.current;
      if (!segs || segs.length <= segmentIndex + 1) return;
      const minTime = Math.ceil((segs[segmentIndex].startMins + MIN_PERIOD_MINUTES) / 60) * 60;
      const maxTime = Math.floor((segs[segmentIndex + 1].endMins - MIN_PERIOD_MINUTES) / 60) * 60;
      const clampedTime = Math.max(minTime, Math.min(maxTime, newTime));
      
      setDragPosition({ x: moveEvent.clientX, y: moveEvent.clientY, time: minutesToDisplay(clampedTime) });
      
      const newSegs = segs.map((segment, idx) => {
        if (idx === segmentIndex) {
          return { ...segment, endMins: clampedTime };
        }
        if (idx === segmentIndex + 1) {
          return { ...segment, startMins: clampedTime };
        }
        return segment;
      });
      setSegments(newSegs);
      segmentsRef.current = newSegs;
    };
    
    const handleMouseUp = () => {
      setDragging(false);
      setDragPosition(null);
      dragStateRef.current.isDragging = false;
      dragStateRef.current.segmentIndex = -1;
      dragStateRef.current.cleanup = null;
      
      // Mark that user has made changes
      setHasUnsavedChanges(true);

      const segs = segmentsRef.current;
      if (!segs || segs.length <= segmentIndex + 1) {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        return;
      }

      const left = segs[segmentIndex];
      const right = segs[segmentIndex + 1];

      // Calculate hour for right segment's start
      const getHour = (mins) => ((Math.floor((mins + DAY_START_HOUR * 60) % MINUTES_IN_DAY / 60)) % 24);
      const pad = (n) => String(n).padStart(2, "0");

      // End time for left: previous hour's 59:59
      const leftEndHour = (getHour(right.startMins) + 23) % 24;
      const newLeftEnd = `${pad(leftEndHour)}:59:59`;

      // Start time for right: current hour's 00:00
      const rightStartHour = getHour(right.startMins);
      const newRightStart = `${pad(rightStartHour)}:00:00`;

      if (onEditPeriodBoundary) {
        onEditPeriodBoundary(left.id, newLeftEnd, right.id, newRightStart);
      }

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    // Store cleanup function in ref so it can be called from visibility change handler
    dragStateRef.current.cleanup = handleMouseUp;
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [onEditPeriodBoundary]);

  // Helper: format label
  const getSegmentLabel = (segment, i, arr) => {
    if (arr.length === 1) return "All Day";

    // Convert internal minutes to real time
    const getHourMin = (mins) => {
      const totalMinutes = (mins + DAY_START_HOUR * 60) % MINUTES_IN_DAY;
      const hour = Math.floor(totalMinutes / 60);
      const min = totalMinutes % 60;
      return { hour, min };
    };

    // Display start time
    const startObj = getHourMin(segment.startMins);
    const endObj = getHourMin(segment.endMins);

    // Display end time: if end minute is 59, show next hour
    let displayEndHour = endObj.hour;
    let displayEndMin = endObj.min;
    if (endObj.min === 59) {
      displayEndHour = (endObj.hour + 1) % 24;
      displayEndMin = 0;
    }

    // Format for display
    const formatDisplay = (hour, min) => {
      if (min === 0) {
        if (hour === 0) return "12AM";
        if (hour === 12) return "12PM";
        if (hour < 12) return `${hour}AM`;
        return `${hour - 12}PM`;
      }
      // If you want to show minutes, you can add that here
      return `${hour}:${String(min).padStart(2, "0")}`;
    };

    return `${formatDisplay(startObj.hour, startObj.min)}-${formatDisplay(displayEndHour, displayEndMin)}`;
  };

  const selectedSegment = segments.find((s) => s.id === selectedPeriod?.id);

  const canAddPeriod = periods.length < 3;
  const canDeletePeriod = periods.length > 1;

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [copySettings, setCopySettings] = useState(true);

  useEffect(() => {
    if (showAddDialog) {
      setCopySettings(true);
    }
  }, [showAddDialog]);

  return (
    <Flex style={{ width: "100%" }}>
      <Flex direction="row" gap="2" style={{ width: "100%", alignItems: "center", maxHeight: "32px" }}>
        <Flex ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", position: "relative" }}>
        {segments.map((segment, index, arr) => {
          const segmentDiv = (
            <div
              key={segment.id}
              className="period-bar-segment"
              data-segment-id={segment.id}
              style={{
                width: `${((segment.endMins - segment.startMins) / MINUTES_IN_DAY) * 100}%`,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                background: segment.id === selectedSegment?.id ? "var(--blue-a3)" : "var(--color-panel-solid)",
                border: segment.id === selectedSegment?.id ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                borderLeft: index > 0 ? (segment.id === selectedSegment?.id ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)") : segment.id === selectedSegment?.id ? "1px solid var(--blue-a6)" : "1px solid var(--gray-a6)",
                borderRadius: arr.length === 1 ? "var(--radius-2)" : index === 0 ? "var(--radius-2) 0 0 var(--radius-2)" : index === arr.length - 1 ? "0 var(--radius-2) var(--radius-2) 0" : "0",
                padding: "8px",
                cursor: "default",
                transition: "width 0.2s cubic-bezier(.4,0,.2,1)",
                overflow: "hidden",
              }}
              onClick={() => onPeriodClick && onPeriodClick(periods[index])}
            >
              <span style={{
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
                textAlign: "center"
              }}>
                <Text size="2" weight="regular">
                  {getSegmentLabel(segment, index, arr)}
                </Text>
              </span>
              {index < segments.length - 1 && (
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    width: "8px",
                    marginRight: "-4px",
                    zIndex: 10,
                    cursor: "col-resize",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseDown={(e) => handleBorderMouseDown(e, index)}
                >
                  <div
                    style={{
                      width: dragging ? "4px" : "1px",
                      height: "100%",
                      background: segment.id === selectedSegment?.id || segments[index + 1]?.id === selectedSegment?.id ? "var(--blue-a6)" : "var(--gray-a6)",
                      transition: "width 0.15s cubic-bezier(.4,0,.2,1)",
                      borderRadius: "2px",
                    }}
                  />
                </div>
              )}
            </div>
          );

          return truncatedSegments.has(segment.id)
            ? (
              <Tooltip content={getSegmentLabel(segment, index, arr)} key={segment.id}>
                {segmentDiv}
              </Tooltip>
            )
            : segmentDiv;
        })}
        </Flex>
        {/* Delete button */}
        <Tooltip content={!canDeletePeriod ? "At least one schedule is required" : "Delete selected schedule"}>
          <IconButton
            type="button"
            size="2"
            variant="soft"
            color="gray"
            disabled={isPeriodLoading}
            style={{ opacity: !canDeletePeriod ? 0.5 : 1 }}
            onClick={() => {
              if (canDeletePeriod && selectedPeriod) {
                setShowDeleteDialog(true);
              }
            }}
          >
            <TrashIcon width="16" height="16" />
          </IconButton>
        </Tooltip>
        {/* Delete AlertDialog */}
        <AlertDialog.Root open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialog.Content maxWidth="400px">
            <AlertDialog.Title>Delete selected schedule</AlertDialog.Title>
            <AlertDialog.Description size="2">All portal settings will be deleted for the selected schedule. This cannot be undone.</AlertDialog.Description>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button
                  variant="solid"
                  color="red"
                  onClick={() => {
                    setShowDeleteDialog(false);
                    if (canDeletePeriod && selectedPeriod) {
                      onDeletePeriod(selectedPeriod.id, selectedPeriod.period);
                    }
                  }}
                >
                  Delete
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
        {/* Add button */}
        <Tooltip content={!canAddPeriod ? "Maximum 3 schedules" : "Add schedule"}>
          <IconButton type="button" size="2" variant="soft" color="blue" disabled={isPeriodLoading} style={{ opacity: !canAddPeriod ? 0.5 : 1 }} onClick={() => setShowAddDialog(true)}>
            <PlusIcon width="16" height="16" />
          </IconButton>
        </Tooltip>
        {/* Add AlertDialog */}
        <AlertDialog.Root 
          open={showAddDialog} 
          onOpenChange={(open) => {
            setShowAddDialog(open);
          }}
        >
          <AlertDialog.Content maxWidth="400px">
            <AlertDialog.Title>Add New Schedule</AlertDialog.Title>
            <AlertDialog.Description size="2">
              A new schedule will be added to this portal, allowing you to set different portal settings for a different time of the day.
            </AlertDialog.Description>
            <Flex gap="3" mt="4" direction="row">
              <Text as="label" size="2">
                <Flex gap="2">
                  <Checkbox 
                    checked={copySettings} 
                    onCheckedChange={(checked) => {
                      setCopySettings(checked);
                    }} 
                  />
                  Copy settings of current schedule
                </Flex>
              </Text>
            </Flex>
            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button
                  variant="solid"
                  color="blue"
                  onClick={() => {
                    setShowAddDialog(false);
                    if (canAddPeriod) {
                      onAddPeriod(copySettings);
                    }
                  }}
                >
                  Add
                </Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Flex>
      {/* Drag position tooltip */}
      {dragPosition && (
        <div
          style={{
            position: "fixed",
            left: dragPosition.x + 10,
            top: dragPosition.y - 30,
            background: "var(--color-panel-solid)",
            border: "1px solid var(--gray-a6)",
            borderRadius: "var(--radius-2)",
            padding: "4px 8px",
            fontSize: "12px",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          {dragPosition.time}
        </div>
      )}
    </Flex>
  );
};

export default PeriodSelector;